import React, { useState, useEffect } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  useDroppable,
  pointerWithin
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { getData, putData } from '../helpers/ApiSettings';

const SortableMenuItem = ({ id, item, index, parentId = null, onEdit, onDelete, onAddSubmenu }) => {
  const [isOver, setIsOver] = useState(false);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id });

  // Setup droppable area for submenu
  const { setNodeRef: setDroppableRef, isOver: isOverDroppable } = useDroppable({
    id: `droppable-${id}`,
    data: {
      type: 'submenu',
      parentIndex: parentId !== null ? parentId : index
    }
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    position: 'relative',
  };

  return (
    <div ref={setNodeRef} style={style} className="card mb-2">
      {/* Droppable zone indicator */}
      {!item.isSubmenuItem && (
        <div
          ref={setDroppableRef}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: isOverDroppable ? 'rgba(0, 123, 255, 0.1)' : 'transparent',
            border: isOverDroppable ? '2px dashed #007bff' : 'none',
            borderRadius: '4px',
            pointerEvents: 'none',
            zIndex: 1
          }}
        />
      )}
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center">
            <span
              {...attributes}
              {...listeners}
              className="me-3 text-muted"
              style={{ cursor: 'grab' }}
            >
              ⋮⋮
            </span>
            <div>
              <h6 className="mb-0">{item.name}</h6>
              <small className="text-muted">{item.link}</small>
            </div>
          </div>
          <div>
            {!item.isSubmenuItem && (
              <button
                onClick={() => onAddSubmenu(index)}
                className="btn btn-sm btn-outline-success me-2"
              >
                + Sous-menu
              </button>
            )}
            <button
              onClick={() => onEdit(item, index, parentId)}
              className="btn btn-sm btn-outline-primary me-2"
            >
              Modifier
            </button>
            <button
              onClick={() => onDelete(index, parentId)}
              className="btn btn-sm btn-outline-danger"
            >
              Supprimer
            </button>
          </div>
        </div>

        {item.submenu && item.submenu.length > 0 && (
          <div className="mt-2 ms-4 border-start ps-3">
            <SortableContext
              items={item.submenu.map((_, i) => `submenu-${index}-${i}`)}
              strategy={verticalListSortingStrategy}
            >
              {item.submenu.map((subItem, subIndex) => (
                <SortableMenuItem
                  key={`submenu-${index}-${subIndex}`}
                  id={`submenu-${index}-${subIndex}`}
                  item={subItem}
                  index={subIndex}
                  parentId={index}
                  onEdit={onEdit}
                  onDelete={onDelete}
                />
              ))}
            </SortableContext>
          </div>
        )}
      </div>
    </div>
  );
};

const SortablePage = ({ id, page }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="list-group-item list-group-item-action"
      {...attributes}
      {...listeners}
    >
      {page.title}
    </div>
  );
};

const NavigationEditor = () => {
  const [menuItems, setMenuItems] = useState([]);
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [submenuModalOpen, setSubmenuModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editingSubmenuItem, setEditingSubmenuItem] = useState(null);
  const [activeParentItem, setActiveParentItem] = useState(null);
  const [selectedMenu, setSelectedMenu] = useState('navigation');
  const [formData, setFormData] = useState({
    name: '',
    link: '',
    isCustom: false,
    hasSubmenu: false
  });
  const [submenuFormData, setSubmenuFormData] = useState({
    name: '',
    link: ''
  });

  const menuOptions = [
    { value: 'navigation', label: 'Menu Principal' },
    { value: 'bottom-navigation', label: 'Menu du footer' },
    { value: 'legal-navigation', label: 'Menu légal' }
  ];

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [navResponse, pagesResponse] = await Promise.all([
          getData(`/${selectedMenu}`),
          getData('/pages?sort[0]=title:asc')
        ]);
        setMenuItems(navResponse.data.data.menuItems || []);
        setPages(pagesResponse.data.data);
        setError(null);
      } catch (err) {
        setError('Erreur lors du chargement des données');
        console.error('Error fetching data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [selectedMenu]);

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (!over) {
      // If dragging a submenu item and dropping outside (not over anything),
      // promote it to a main menu item
      const draggedId = active.id.toString();
      if (draggedId.startsWith('submenu-')) {
        const [, parentIndex, subIndex] = draggedId.split('-').map(Number);
        const subItem = menuItems[parentIndex]?.submenu?.[subIndex];

        if (subItem) {
          const newItems = [...menuItems];
          // Remove from submenu
          const [removedItem] = newItems[parentIndex].submenu.splice(subIndex, 1);
          if (newItems[parentIndex].submenu.length === 0) {
            delete newItems[parentIndex].submenu;
          }
          // Add to main menu
          removedItem.isSubmenuItem = false;
          newItems.push(removedItem);
          setMenuItems(newItems);
        }
      }
      return;
    }

    // Handle dropping into submenu area
    if (over.id.toString().startsWith('droppable-')) {
      const overId = over.id.toString();
      const targetId = overId.replace('droppable-', '');
      let targetIndex = parseInt(targetId.split('-')[1]);
      const draggedId = active.id.toString();

      // Don't allow dropping onto itself or its own submenu items
      if (draggedId === targetId ||
        (draggedId.startsWith('menu-') &&
          menuItems[parseInt(draggedId.split('-')[1])]?.submenu?.some(item =>
            item.submenu?.some(subItem => subItem.id === parseInt(targetId.split('-')[1]))
          ))) {
        return;
      }

      let newItem;
      let newItems = [...menuItems];

      if (draggedId.startsWith('page-')) {
        // Handle dragging from pages list
        const pageId = parseInt(draggedId.split('-')[1]);
        const page = pages.find(p => p.id === pageId);
        if (!page) return;

        newItem = {
          name: page.title,
          link: `/page/${page.slug}`,
          isCustom: false,
          isSubmenuItem: true
        };
      } else if (draggedId.startsWith('menu-')) {
        // Handle dragging from main menu
        const sourceIndex = parseInt(draggedId.split('-')[1]);
        const sourceItem = { ...newItems[sourceIndex] };

        // Remove the original item first
        newItems.splice(sourceIndex, 1);

        newItem = {
          ...sourceItem,
          isSubmenuItem: true
        };

        // If moving an item that comes before the target, adjust the target index
        if (sourceIndex < targetIndex) {
          targetIndex--;
        }
      } else if (draggedId.startsWith('submenu-')) {
        // Handle dragging from another submenu
        const [, oldParentIndex, oldSubIndex] = draggedId.split('-').map(Number);
        const sourceItem = { ...newItems[oldParentIndex].submenu[oldSubIndex] };

        // Remove from old submenu
        newItems[oldParentIndex].submenu.splice(oldSubIndex, 1);
        if (newItems[oldParentIndex].submenu.length === 0) {
          delete newItems[oldParentIndex].submenu;
        }

        newItem = sourceItem;
      }

      // Add to new submenu
      if (!newItems[targetIndex].submenu) {
        newItems[targetIndex].submenu = [];
      }
      newItems[targetIndex].submenu.push(newItem);
      setMenuItems(newItems);
      return;
    }

    // Handle page dragging
    if (active.id.toString().startsWith('page-')) {
      const pageId = parseInt(active.id.split('-')[1]);
      const page = pages.find(p => p.id === pageId);

      if (!page) return;

      const newMenuItem = {
        name: page.title,
        link: `/page/${page.slug}`,
        isCustom: false
      };

      // Check if dropping into submenu
      if (over.id.toString().startsWith('submenu-')) {
        const [, parentIndex, submenuIndex] = over.id.split('-').map(Number);
        const newItems = [...menuItems];
        if (!newItems[parentIndex].submenu) {
          newItems[parentIndex].submenu = [];
        }
        newItems[parentIndex].submenu.splice(submenuIndex, 0, {
          ...newMenuItem,
          isSubmenuItem: true
        });
        setMenuItems(newItems);
      } else {
        // Dropping into main menu
        const overIndex = parseInt(over.id.split('-')[1]);
        const newItems = [...menuItems];
        newItems.splice(overIndex, 0, newMenuItem);
        setMenuItems(newItems);
      }
      return;
    }

    // Handle menu item reordering
    if (active.id !== over.id) {
      const oldIndex = parseInt(active.id.split('-')[1]);
      const newIndex = parseInt(over.id.split('-')[1]);

      if (active.id.startsWith('submenu-') && over.id.startsWith('submenu-')) {
        // Reordering within same submenu
        const [, activeParentIndex, activeSubIndex] = active.id.split('-').map(Number);
        const [, overParentIndex, overSubIndex] = over.id.split('-').map(Number);

        if (activeParentIndex === overParentIndex) {
          // Reordering within the same submenu
          const newItems = [...menuItems];
          const submenu = [...newItems[activeParentIndex].submenu];
          newItems[activeParentIndex].submenu = arrayMove(submenu, activeSubIndex, overSubIndex);
          setMenuItems(newItems);
        } else {
          // Moving between different submenus
          const newItems = [...menuItems];
          const sourceSubmenu = newItems[activeParentIndex].submenu;
          const [movedItem] = sourceSubmenu.splice(activeSubIndex, 1);

          if (sourceSubmenu.length === 0) {
            delete newItems[activeParentIndex].submenu;
          }

          if (!newItems[overParentIndex].submenu) {
            newItems[overParentIndex].submenu = [];
          }
          newItems[overParentIndex].submenu.splice(overSubIndex, 0, movedItem);
          setMenuItems(newItems);
        }
      } else if (active.id.startsWith('submenu-') && over.id.startsWith('menu-')) {
        // Moving submenu item to main menu
        const [, parentIndex, subIndex] = active.id.split('-').map(Number);
        const overIndex = parseInt(over.id.split('-')[1]);

        const newItems = [...menuItems];
        const [movedItem] = newItems[parentIndex].submenu.splice(subIndex, 1);

        if (newItems[parentIndex].submenu.length === 0) {
          delete newItems[parentIndex].submenu;
        }

        movedItem.isSubmenuItem = false;
        newItems.splice(overIndex, 0, movedItem);
        setMenuItems(newItems);
      } else {
        // Reordering main menu
        setMenuItems(arrayMove(menuItems, oldIndex, newIndex));
      }
    }
  };

  const handleSave = async () => {
    try {
      await putData(`/${selectedMenu}`, {
        data: {
          menuItems
        }
      });
      alert('Navigation sauvegardée avec succès');
    } catch (err) {
      alert('Erreur lors de la sauvegarde');
      console.error('Error saving navigation:', err);
    }
  };

  const handleAddCustom = () => {
    setEditingItem(null);
    setFormData({
      name: '',
      link: '',
      isCustom: true,
      hasSubmenu: false
    });
    setModalOpen(true);
  };

  const handleEdit = (item, index, parentIndex = null) => {
    if (parentIndex !== null) {
      setEditingSubmenuItem({ parentIndex, subIndex: index });
      setSubmenuFormData({
        name: item.name,
        link: item.link
      });
      setSubmenuModalOpen(true);
    } else {
      setEditingItem(index);
      setFormData({
        name: item.name,
        link: item.link,
        isCustom: item.isCustom || false,
        hasSubmenu: !!item.submenu
      });
      setModalOpen(true);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newItem = {
      name: formData.name,
      link: formData.link,
      isCustom: formData.isCustom
    };

    if (formData.hasSubmenu) {
      newItem.submenu = [];
    }

    if (editingItem !== null) {
      const newItems = [...menuItems];
      // Preserve existing submenu if it exists
      if (newItems[editingItem].submenu) {
        newItem.submenu = newItems[editingItem].submenu;
      }
      newItems[editingItem] = newItem;
      setMenuItems(newItems);
    } else {
      setMenuItems([...menuItems, newItem]);
    }

    setModalOpen(false);
    setFormData({
      name: '',
      link: '',
      isCustom: false,
      hasSubmenu: false
    });
  };

  const handleSubmenuSubmit = (e) => {
    e.preventDefault();
    if (editingSubmenuItem) {
      // Edit existing submenu item
      const newItems = [...menuItems];
      newItems[editingSubmenuItem.parentIndex].submenu[editingSubmenuItem.subIndex] = {
        name: submenuFormData.name,
        link: submenuFormData.link
      };
      setMenuItems(newItems);
      setEditingSubmenuItem(null);
    } else if (activeParentItem !== null) {
      // Add new submenu item
      const newItems = [...menuItems];
      if (!newItems[activeParentItem].submenu) {
        newItems[activeParentItem].submenu = [];
      }
      newItems[activeParentItem].submenu.push({
        name: submenuFormData.name,
        link: submenuFormData.link
      });
      setMenuItems(newItems);
    }
    setSubmenuFormData({ name: '', link: '' });
    setSubmenuModalOpen(false);
  };

  const handleDelete = (index, parentId = null) => {
    if (parentId !== null) {
      // Deleting a submenu item
      if (window.confirm('Êtes-vous sûr de vouloir supprimer cet élément du sous-menu ?')) {
        const newItems = [...menuItems];
        newItems[parentId].submenu.splice(index, 1);
        if (newItems[parentId].submenu.length === 0) {
          delete newItems[parentId].submenu;
        }
        setMenuItems(newItems);
      }
    } else {
      // Deleting a main menu item
      if (window.confirm('Êtes-vous sûr de vouloir supprimer cet élément ?')) {
        const newItems = [...menuItems];
        newItems.splice(index, 1);
        setMenuItems(newItems);
      }
    }
  };

  const handleMenuChange = (e) => {
    setSelectedMenu(e.target.value);
  };

  if (loading) {
    return (
      <div className="text-center p-5">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Chargement...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        {error}
      </div>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={pointerWithin}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className="row">
        <div className="col-md-8">
          <div className="card mb-4">
            <div className="card-header d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center gap-3">
                  <select 
                    className="form-select" 
                    value={selectedMenu} 
                    onChange={handleMenuChange}
                    style={{ width: 'auto' }}
                  >
                    {menuOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              <h5 className="mb-0">Menu Principal</h5>
              <button onClick={handleSave} className="btn btn-primary">
                Sauvegarder
              </button>
            </div>
            <div className="card-body">
              <SortableContext
                items={menuItems.map((_, i) => `menu-${i}`)}
                strategy={verticalListSortingStrategy}
              >
                {menuItems.map((item, index) => (
                  <SortableMenuItem
                    key={`menu-${index}`}
                    id={`menu-${index}`}
                    item={item}
                    index={index}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    onAddSubmenu={(index) => {
                      setActiveParentItem(index);
                      setEditingSubmenuItem(null);
                      setSubmenuModalOpen(true);
                    }}
                  />
                ))}
              </SortableContext>
            </div>
          </div>
        </div>

        <div className="col-md-4">
          <div className="card mb-4">
            <div className="card-header">
              <h5 className="mb-0">Pages Disponibles</h5>
            </div>
            <div className="card-body">
              <button
                onClick={handleAddCustom}
                className="btn btn-outline-primary w-100 mb-3"
              >
                + Lien personnalisé
              </button>
              <SortableContext
                items={pages.map(page => `page-${page.id}`)}
                strategy={verticalListSortingStrategy}
              >
                <div className="list-group">
                  {pages.map((page) => (
                    <SortablePage
                      key={`page-${page.id}`}
                      id={`page-${page.id}`}
                      page={page}
                    />
                  ))}
                </div>
              </SortableContext>
            </div>
          </div>
        </div>

        {/* Main Modal */}
        {modalOpen && (
          <div
            className="modal show"
            style={{
              display: 'block',
              backgroundColor: 'rgba(0,0,0,0.5)',
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              zIndex: 1050,
              overflow: 'auto'
            }}
          >
            <div className="modal-dialog">
              <div className="modal-content">
                <form onSubmit={handleSubmit}>
                  <div className="modal-header">
                    <h5 className="modal-title">
                      {editingItem !== null ? 'Modifier le menu' : 'Nouveau lien'}
                    </h5>
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => setModalOpen(false)}
                    ></button>
                  </div>
                  <div className="modal-body">
                    <div className="mb-3">
                      <label htmlFor="name" className="form-label">Nom affiché</label>
                      <input
                        type="text"
                        className="form-control"
                        id="name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                      />
                    </div>
                    <div className="mb-3">
                      <label htmlFor="link" className="form-label">Lien</label>
                      <input
                        type="text"
                        className="form-control"
                        id="link"
                        value={formData.link}
                        onChange={(e) => setFormData({ ...formData, link: e.target.value })}
                        required
                      />
                    </div>
                    {!formData.isSubmenuItem && (
                      <div className="mb-3 form-check">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="hasSubmenu"
                          checked={formData.hasSubmenu}
                          onChange={(e) => setFormData({ ...formData, hasSubmenu: e.target.checked })}
                        />
                        <label className="form-check-label" htmlFor="hasSubmenu">
                          A un sous-menu
                        </label>
                      </div>
                    )}
                  </div>
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => setModalOpen(false)}
                    >
                      Annuler
                    </button>
                    <button type="submit" className="btn btn-primary">
                      {editingItem !== null ? 'Mettre à jour' : 'Ajouter'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Submenu Modal */}
        {submenuModalOpen && (
          <div
            className="modal show"
            style={{
              display: 'block',
              backgroundColor: 'rgba(0,0,0,0.5)',
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              zIndex: 1060,
              overflow: 'auto'
            }}
          >
            <div className="modal-dialog">
              <div className="modal-content">
                <form onSubmit={handleSubmenuSubmit}>
                  <div className="modal-header">
                    <h5 className="modal-title">
                      {editingSubmenuItem
                        ? 'Modifier l\'élément du sous-menu'
                        : 'Ajouter un élément au sous-menu'
                      }
                    </h5>
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => {
                        setSubmenuModalOpen(false);
                        setEditingSubmenuItem(null);
                      }}
                    ></button>
                  </div>
                  <div className="modal-body">
                    <div className="mb-3">
                      <label htmlFor="submenuName" className="form-label">Nom affiché</label>
                      <input
                        type="text"
                        className="form-control"
                        id="submenuName"
                        value={submenuFormData.name}
                        onChange={(e) => setSubmenuFormData({ ...submenuFormData, name: e.target.value })}
                        required
                      />
                    </div>
                    <div className="mb-3">
                      <label htmlFor="submenuLink" className="form-label">Lien</label>
                      <input
                        type="text"
                        className="form-control"
                        id="submenuLink"
                        value={submenuFormData.link}
                        onChange={(e) => setSubmenuFormData({ ...submenuFormData, link: e.target.value })}
                        required
                      />
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => {
                        setSubmenuModalOpen(false);
                        setEditingSubmenuItem(null);
                      }}
                    >
                      Annuler
                    </button>
                    <button type="submit" className="btn btn-primary">
                      {editingSubmenuItem ? 'Mettre à jour' : 'Ajouter'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>

      <DragOverlay>
        {activeId ? (
          <div className="card">
            <div className="card-body">
              {activeId.startsWith('page-') ? (
                pages.find(p => `page-${p.id}` === activeId)?.title
              ) : (
                menuItems[parseInt(activeId.split('-')[1])]?.name
              )}
            </div>
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};

export default NavigationEditor;